<template>
  <div>
    <transition name="fade" mode="out-in">
      <div class="add-new-service" v-if="currentStep === 'step-form'" :key="'step1'">
        <h2 class="title">{{ $t('servicesManagementPopup.chooseServiceToAdd') }}</h2>
        <ul role="menubar" class="form-tabs">
          <li>
            <a href="#" @click.prevent="setActiveTab(1)" :class="{ 'active': activeTab === 1 }">
              {{ $t('registration.tabNameMobile') }}
            </a>
          </li>
          <li>
            <a href="#" @click.prevent="setActiveTab(2)" :class="{ 'active': activeTab === 2 }">
              {{ $t('registration.tabNameCable') }}
            </a>
          </li>
        </ul>
        <registration-form ref="registrationForm"
                           tabs-hidden
                           is-connect-service-form
                           class="service-form"
                           @cancel-add="cancelAddingService"
                           @reconnection-mobile="checkReconnectionMobile"
                           @reconnection-cable="checkReconnectionCable"/>
      </div>
      <div class="add-new-service password-step" v-if="currentStep === 'step-password'" :key="'step2'">
        <h2 class="title">{{ $t('servicesManagementPopup.addContract') }}</h2>
        <template v-if="reconnectionType ===  'mobile'">
          <h3 class="subtitle">{{ $t('servicesManagementPopup.mobileServiceTitle') }}</h3>
          <p class="description" v-html="$t('servicesManagementPopup.addMobileServiceDescription1', { phoneNumber: userPhoneNumber, email: userEmail})"></p>
        </template>
        <template v-else>
          <h3 class="subtitle">{{ $t('servicesManagementPopup.cableServiceTitle') }}</h3>
          <p class="description" v-html="$t('servicesManagementPopup.addCableServiceDescription1', { email: userEmail})"></p>
        </template>
        <p class="description" v-html="$t('servicesManagementPopup.addServiceDescription2', {email: userEmail})"></p>
        <validation-observer ref="passwordObs"
                             tag="form"
                             class="service-form"
                             @submit.prevent="submitRegistrationRequestWithPassword">
          <base-v-input v-model.trim="userPassword"
                        id="password"
                        name="password"
                        rules="required"
                        container-class="text-bold"
                        :submitted="passwordSubmitted"
                        :label="$t('registration.passwordLabel')"
                        revealPassword
                        variant="white"
                        type="password"
                        maxlength="20"
                        vid="password"
                        ref="password"/>
          <div class="action-buttons">
            <a href="#" class="back-link" @click.prevent="cancelAddingService">{{ $t('common.cancel') }}</a>
            <base-button variant="primary" type="submit" :disabled="!userPassword.length || dataLoading" :loading="dataLoading">{{ $t('common.continue') }}</base-button>
          </div>
        </validation-observer>
      </div>
    </transition>
  </div>
</template>

<script>
import RegistrationForm from '@/components/registration/registration-form'
import errorMessageHandler from '@/mixins/errorMessageHandler'
import { makeQueryString } from '@/utils/queryParser'
import { lsRemove } from '@/utils/localStorageController'

const steps = {
  form: 'step-form',
  password: 'step-password'
}

export default {
  name: 'connect-new-service',
  components: { RegistrationForm },
  data () {
    return {
      activeTab: 1,
      customerId: null,
      currentStep: '',
      userPassword: '',
      passwordSubmitted: false,
      dataLoading: false,
      userPhoneNumber: '',
      userEmail: '',
      userOtpCode: '',
      reconnectionType: 'mobile'
    }
  },
  mixins: [errorMessageHandler],
  props: {
    connectionData: {
      type: Function,
      default: () => {
        return {}
      }
    }
  },
  methods: {
    setActiveTab (tabNumber) {
      this.activeTab = tabNumber
      this.$refs.registrationForm.activeTab = tabNumber
    },
    cancelAddingService (refresh = false) {
      this.$emit('cancel-add', refresh)
    },
    fetchUserData () {
      this.$mvcRequest.get('users/userinfo')
        .then(response => {
          this.customerId = response.data.CustomerId || '2415677' // TODO remove this hardcoded value later, when we have good data
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
    },
    checkReconnectionMobile (data) {
      this.userPhoneNumber = data.phoneNumber
      this.userOtpCode = data.otpCode
      this.reconnectionType = data.type
      this.customerId = data.userId
      this.userEmail = data.email

      if (!this.customerId || !this.userEmail) {
        this.getReconnectionMobileData()
      } else {
        if (data.registered) {
          this.currentStep = steps.password
        } else {
          this.sendMobileRegistrationRequest()
        }
      }
    },
    getReconnectionMobileData () {
      this.$apiRequest.get('user/service/connection/checkmobile?msisdn=' + this.userPhoneNumber)
        .then(response => {
          this.userEmail = response.data.email
          this.customerId = response.data.customerId
          if (response.data.registered) {
            // go to third step
            this.currentStep = steps.password
          } else {
            this.sendMobileRegistrationRequest()
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
    },
    async submitRegistrationRequestWithPassword () {
      this.passwordSubmitted = true

      const fieldValid = await this.$refs.passwordObs.validate()

      if (fieldValid) {
        this.dataLoading = true
        if (this.reconnectionType === 'mobile') {
          this.sendMobileRegistrationRequest(this.userPassword)
        } else {
          this.sendCableRegistrationRequest(this.userPassword)
        }
      } else {
        this.dataLoading = false
      }
    },
    sendMobileRegistrationRequest (password) {
      const dataToSend = {
        msisdn: this.userPhoneNumber,
        otpRequestCode: this.userOtpCode,
        login: this.userEmail,
        customerId: this.customerId
      }

      if (password) dataToSend.password = password

      this.$apiRequest.post('user/service/connection/mobile', dataToSend)
        .then(response => {
          if (response.status === 200) {
            this.toast('success', {
              title: this.$t('servicesManagementPopup.successMessage.title'),
              message: this.$t('servicesManagementPopup.successMessage.message')
            })
            this.cancelAddingService(true)
            this.$props.connectionData()
            lsRemove('sc_menu_services')
          } else {
            this.toast('error', {
              message: this.$t('errors.loginFail')
            })
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
        .finally(() => {
          this.dataLoading = false
        })
    },
    checkReconnectionCable (data) {
      const { customerNumber, invoiceNumber } = data
      this.reconnectionType = data.type

      const qs = makeQueryString({ customerId: customerNumber, invoiceNumber: invoiceNumber })
      this.$apiRequest.get(`user/service/connection/checkfixed${qs}`)
        .then(response => {
          this.userEmail = response.data.email
          this.customerId = response.data.customerId
          if (response.data.registered) {
            // go to third step
            this.currentStep = steps.password
          } else {
            this.sendCableRegistrationRequest()
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
    },
    sendCableRegistrationRequest (password) {
      const dataToSend = {
        customerId: this.customerId,
        login: this.userEmail
      }

      if (password) dataToSend.password = password

      this.$apiRequest.post('user/service/connection/fixed', dataToSend)
        .then(response => {
          if (response.status === 200) {
            this.toast('success', {
              title: this.$t('servicesManagementPopup.successMessage.title'),
              message: this.$t('servicesManagementPopup.successMessage.message')
            })
            this.cancelAddingService(true)
            this.$props.connectionData()
            lsRemove('sc_menu_services')
          } else {
            this.toast('error', {
              message: this.$t('errors.loginFail')
            })
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
        .finally(() => {
          this.dataLoading = false
        })
    }
  },
  created () {
    // this.fetchUserData()
    this.currentStep = steps.form
  }
}
</script>

<style scoped lang="scss">
.add-new-service {
  .title {
    font-size: 2rem;
    font-weight: 600;
    margin-top: 1.6rem;
    margin-bottom: 2rem;

    @media (map_get($breakpoints, sm)) {
      font-size: 2.4rem;
      font-weight: 600;
      margin-top: 1.6rem;
      margin-bottom: 4.8rem
    }
  }

  .subtitle {
    font-size: 1.6rem;
    font-weight: 600;
    text-transform: uppercase;
    padding-bottom: 0.8rem;
    margin-bottom: 3.2rem;
    border-bottom: 1px solid $border;
  }

  .description {
    font-size: 1.6rem;
    line-height: 1.75;
    margin-bottom: 2.4rem;
    width: 100%;
    max-width: 560px;
  }

  ul.form-tabs {
    margin-bottom: 4rem;
    display: flex;
    width: 100%;
    list-style-type: none;
    border-bottom: 1px solid $border;

    li {
      flex-basis: 50%;
      max-width: 160px;
      margin-bottom: -1px;
    }

    a {
      display: block;
      cursor: pointer;
      font-weight: 600;
      text-transform: uppercase;
      text-align: center;
      position: relative;
      font-size: 1.6rem;
      line-height: 1.8;
      padding: 1.2rem;
      color: $text;
      text-decoration: none;

      @media (map_get($breakpoints, sm)) {
        padding: 1.3rem;
      }

      &.active {
        color: $mtel-red;
        border-color: $mtel-red;
        box-shadow: inset 0 -2px 0 $mtel-red;
        cursor: default;
      }

    }
  }

  .service-form {
    width: 100%;
    padding: 0;
    margin: 0 0 2rem;
    background-color: transparent;

    @media (map_get($breakpoints, sm)) {
      max-width: 320px;
    }

    .action-buttons {
      display: flex;
      flex-direction: column;
      align-items: center;

      @media (map_get($breakpoints, sm)) {
        flex-direction: row;
        justify-content: flex-end;
      }

      a.back-link {
        display: block;
        margin-top: 2.4rem;
        padding: 0 3rem;
        margin-right: 1rem;
        font-weight: 600;
        text-decoration: none;

        @media (map_get($breakpoints, sm)) {
          margin-top: 0;
          width: auto;
        }
      }
    }
  }

  &.password-step {
    .title {
      margin-bottom: 5.6rem;
    }
  }
}

</style>
